<template>
  <div>
    <router-link :to="{ name: 'Measurements' }" class="text-sm font-semibold text-brand-600 mb-4 inline-block">
      ← Back to measurements
    </router-link>

    <div v-if="loading" class="py-16 text-center text-ink-soft">
      <font-awesome-icon icon="circle-notch" spin /> Loading…
    </div>

    <div v-else-if="!m" class="py-16 text-center text-ink-muted">Measurement not found.</div>

    <div v-else>
      <header class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 class="text-2xl font-bold">{{ m.title }}</h1>
          <p class="text-ink-soft text-sm mt-1">
            {{ m.patient.first_name }} {{ m.patient.last_name }} ·
            {{ m.patient.external_patient_id }} · {{ fmtDateTime(m.measurement_timestamp) }}
          </p>
        </div>
        <div class="flex gap-2">
          <span class="pill" :class="statusPill(m.validation_status)">{{ m.validation_status || 'n/a' }}</span>
          <span class="pill" :class="statusPill(m.report_status)">{{ m.report_status }}</span>
        </div>
      </header>

      <!-- VPA scores -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Total VPA</p>
          <p class="text-3xl font-extrabold mt-2 text-brand-600">{{ num(m.total_vpa) }}</p>
        </div>
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Free VPA</p>
          <p class="text-3xl font-extrabold mt-2">{{ num(m.free_vpa) }}</p>
        </div>
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Severe seizures</p>
          <p class="text-3xl font-extrabold mt-2">{{ prob(m.vpa_severe_seizures) }}</p>
        </div>
        <div class="card p-5">
          <p class="text-xs font-bold uppercase tracking-wider text-ink-muted">Severe side-effects</p>
          <p class="text-3xl font-extrabold mt-2">{{ prob(m.vpa_severe_side_effects) }}</p>
        </div>
      </div>

      <div class="grid lg:grid-cols-2 gap-6">
        <!-- Details -->
        <div class="card p-6">
          <h2 class="font-bold mb-4">Details</h2>
          <dl class="text-sm">
            <div class="flex justify-between py-2 border-b border-gray-50">
              <dt class="text-ink-soft">Doctor</dt>
              <dd class="font-semibold">{{ m.doctor.first_name }} {{ m.doctor.last_name }}</dd>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-50">
              <dt class="text-ink-soft">Device</dt>
              <dd class="font-semibold">{{ m.device.name }}</dd>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-50">
              <dt class="text-ink-soft">Valproic acid taken</dt>
              <dd class="font-semibold">{{ m.valproic_acid_taken ? 'Yes' : 'No' }}</dd>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-50">
              <dt class="text-ink-soft">Drug usage time</dt>
              <dd class="font-semibold">{{ fmtDateTime(m.drug_usage_time) }}</dd>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-50">
              <dt class="text-ink-soft">Runtime error</dt>
              <dd class="font-semibold">{{ m.runtime_error ? 'Yes' : 'No' }}</dd>
            </div>
            <div v-if="m.message" class="py-2 border-b border-gray-50">
              <dt class="text-ink-soft mb-1">Message</dt>
              <dd>{{ m.message }}</dd>
            </div>
            <div v-if="m.text" class="py-2">
              <dt class="text-ink-soft mb-1">Notes</dt>
              <dd>{{ m.text }}</dd>
            </div>
          </dl>
        </div>

        <!-- Files + figures -->
        <div class="card p-6">
          <h2 class="font-bold mb-4">Files &amp; figures</h2>

          <div v-if="figures.length" class="grid grid-cols-2 gap-3 mb-4">
            <a v-for="f in figures" :key="f" :href="storageUrl(f)" target="_blank" class="block">
              <img
                v-if="isImage(f)"
                :src="storageUrl(f)"
                class="rounded-xl border border-gray-100 w-full h-32 object-cover"
                @error="onImgError"
              />
              <span v-else class="pill pill-muted"><font-awesome-icon icon="file" /> figure</span>
            </a>
          </div>

          <ul class="space-y-2">
            <li v-for="file in files" :key="file.path" class="flex items-center justify-between text-sm">
              <span class="flex items-center gap-2 min-w-0">
                <font-awesome-icon icon="file" class="text-ink-muted" />
                <span class="truncate">{{ file.label }}</span>
              </span>
              <a :href="storageUrl(file.path)" target="_blank" class="btn btn-secondary btn-sm">
                <font-awesome-icon icon="download" /> Open
              </a>
            </li>
            <li v-if="!files.length && !figures.length" class="text-ink-muted">
              No files referenced.
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api, storageUrl } from 'helpers/api'
import { fmtDate, fmtDateTime, statusPill } from 'helpers/format'

export default {
  data() {
    return { m: null, loading: true }
  },
  computed: {
    figures() {
      return (this.m && this.m.data && this.m.data.figures) || []
    },
    files() {
      const d = (this.m && this.m.data) || {}
      const out = []
      const add = (path, label) => {
        if (path) out.push({ path, label })
      }
      add(d.positive_raw_file_path, 'Positive RAW')
      add(d.negative_raw_file_path, 'Negative RAW')
      add(d.positive_co2_file_path, 'Positive CO₂')
      add(d.negative_co2_file_path, 'Negative CO₂')
      return out
    },
  },
  async created() {
    try {
      this.m = await api.getMeasurement(this.$route.params.uuid)
    } catch (e) {
      this.m = null
    } finally {
      this.loading = false
    }
  },
  methods: {
    fmtDate,
    fmtDateTime,
    statusPill,
    storageUrl,
    num(v) {
      return v == null ? '—' : Number(v).toFixed(2)
    },
    prob(v) {
      return v == null ? '—' : (Number(v) * 100).toFixed(0) + '%'
    },
    isImage(f) {
      return /\.(png|jpe?g|gif|webp)$/i.test(f)
    },
    onImgError(e) {
      e.target.style.display = 'none'
    },
  },
}
</script>
